import { useAppMutation } from "../../../http/useAppMutation.ts";
import { useAppQuery } from "../../../http/useAppQuery.ts";
import { DEFAULT_MODELS_UUIDS } from "../../constants.ts";
import { useUser } from "../../hooks/useUser.ts";
import { useWorkspaceUuid } from "../../hooks/useWorkspaceUuid.ts";
import type { ModelWithContents } from "../../types.ts";
import { isUserAllowedTo } from "../../utils/isUserAllowedTo.ts";

type ModelRetrainStatus = "upToDate" | "training" | "outdated" | "unknown";

export const useRetrainModel = ({
  modelUuid,
  onSuccess,
  forFree = false,
}: {
  modelUuid: string;
  onSuccess?: () => void;
  forFree?: boolean;
}) => {
  const { user } = useUser();
  const workspaceUuid = useWorkspaceUuid();
  const { data: model } = useAppQuery<ModelWithContents>({
    queryKey: `references/${modelUuid}`,
  });
  const { mutate: retrainMutate, isLoading } = useAppMutation({
    path: forFree ? "references/retrain/free" : "references/retrain",
    invalidate: [
      `workspaces/${workspaceUuid}/references`,
      `references/${modelUuid}`,
    ],
    onSuccess,
  }).mutation;

  const isUserAllowed =
    (model?.is_curated
      ? user && isUserAllowedTo(user, "references:update-curated-references")
      : !model?.is_curated) &&
    (!forFree || (user && isUserAllowedTo(user, "credits:bypass-consumption")));

  const modelRetrainStatus: ModelRetrainStatus = !model
    ? "unknown"
    : (!model.is_outdated && model.status === "READY") ||
      DEFAULT_MODELS_UUIDS.includes(model.uuid)
    ? "upToDate"
    : model.status === "TRAINING"
    ? "training"
    : "outdated";

  return {
    mutate: () =>
      retrainMutate({ uuid: modelUuid, workspace_uuid: workspaceUuid }),
    isLoading,
    isDisabled: modelRetrainStatus !== "outdated",
    isUserAllowed,
    modelRetrainStatus,
  };
};
